import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ShoppingBag } from "lucide-react";
import { http, imgUrl, priceLabel, useSiteData } from "@/lib/api";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { PageHeader } from "@/components/site/PageHeader";
import { PackGallery } from "@/components/site/PackGallery";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { Footer } from "@/components/site/Footer";
import { EASE, Eyebrow } from "@/components/site/Reveal";

const Mask = ({ children, d = 0, cls = "" }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span className={`block ${cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, ease: EASE, delay: d }}>{children}</motion.span>
  </span>
);

const PackHero = ({ pack, onBuy }) => (
  <section className="grain relative overflow-hidden bg-[#041214] pb-20 pt-32" data-testid="pack-hero">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[#0D9488]/25 blur-[140px]" />
    <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Eyebrow>Pack Gospel · versão {pack.version}</Eyebrow>
        <h1 className="mt-6 text-6xl font-bold uppercase leading-[0.85] text-teal-50 md:text-8xl" data-testid="pack-title">
          <Mask d={0.15} cls="font-display">Pack</Mask>
          <Mask d={0.28} cls="font-serif-i normal-case text-[#2DD4BF]">Gospel {pack.version}</Mask>
        </h1>
        {pack.tag && <span className="font-mono-x mt-6 inline-block rounded-full border border-[#2DD4BF]/50 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#2DD4BF]">{pack.tag}</span>}
        <p className="mt-6 max-w-lg text-base leading-relaxed text-teal-100/85 md:text-lg">{pack.description}</p>
        {pack.features?.length > 0 && (
          <ul className="mt-6 space-y-2">
            {pack.features.map((f) => <li key={f} className="flex gap-2 text-teal-100"><Check className="mt-1 h-4 w-4 shrink-0 text-[#2DD4BF]" />{f}</li>)}
          </ul>
        )}
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <span className="font-display text-3xl text-teal-50" data-testid="pack-page-price">{priceLabel(pack, "Em breve")}</span>
          <button onClick={onBuy} data-testid="pack-page-buy" className="inline-flex items-center gap-2 rounded-full bg-[#2DD4BF] px-7 py-4 font-semibold text-[#041214] transition-transform hover:-translate-y-0.5">
            <ShoppingBag className="h-5 w-5" /> Comprar agora
          </button>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: EASE, delay: 0.3 }} className="lg:col-span-6">
        <div className="relative aspect-square overflow-hidden rounded-[3px] [clip-path:polygon(8%_0,100%_0,100%_90%,92%_100%,0_100%,0_10%)]">
          <img src={imgUrl(pack.image_url || "/img/pack.jpeg")} alt={pack.name} className="h-full w-full object-cover" />
        </div>
      </motion.div>
    </div>
  </section>
);

const OtherVersions = ({ packs, current }) => (
  <div className="mt-24 flex flex-wrap gap-3" data-testid="pack-other-versions">
    {packs.filter((p) => p.id !== current).map((p) => (
      <Link key={p.id} to={`/pack/${p.id}`} data-testid={`pack-other-${p.version}`} className="font-display rounded-full border border-[#163D43] px-6 py-3 text-xl text-teal-50 transition-colors hover:border-[#2DD4BF] hover:text-[#2DD4BF]">
        {p.version || p.name}
      </Link>
    ))}
  </div>
);

export default function PackPage() {
  const { id } = useParams();
  const { packs, settings } = useSiteData();
  const [pack, setPack] = useState(null);
  const [missing, setMissing] = useState(false);
  const [buying, setBuying] = useState(false);
  useEffect(() => {
    setPack(null);
    window.scrollTo(0, 0);
    http.get(`/packs/${id}`).then((r) => setPack(r.data)).catch(() => setMissing(true));
  }, [id]);
  return (
    <div className="min-h-screen bg-[#041214]" data-testid="pack-page">
      <SmoothScroll />
      <PageHeader />
      {missing && <p className="pt-40 text-center text-teal-200">Pack não encontrado.</p>}
      {pack && (
        <>
          <PackHero pack={pack} onBuy={() => setBuying(true)} />
          <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
            <Eyebrow n="↓">Prévias das artes incluídas</Eyebrow>
            <h2 className="font-display mb-12 mt-6 text-4xl font-semibold uppercase text-teal-50 md:text-5xl">O que vem <span className="font-serif-i normal-case text-[#2DD4BF]">no pack</span></h2>
            <PackGallery images={Array.isArray(pack.gallery) ? pack.gallery : []} name={pack.name} />
            <p className="font-mono-x mt-24 text-xs uppercase tracking-[0.22em] text-teal-300">Outras versões</p>
            <OtherVersions packs={packs} current={pack.id} />
          </section>
          <CheckoutDialog key={buying ? "open" : "closed"} item={buying ? pack : null} kind="pack" settings={settings} onClose={() => setBuying(false)} />
        </>
      )}
      <Footer settings={settings} />
      <WhatsAppFloat settings={settings} />
    </div>
  );
}
